// apps/web/src/pages/maintenance/ChecklistWizardPage.tsx
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, ArrowLeft, ArrowRight, Check } from 'lucide-react'
import { PageHeader } from '../../components/layout/PageHeader'
import { Card } from '../../components/ui/Card'
import api from '../../lib/api'

interface Site { id: string; name: string }
interface Asset { id: string; name: string; assetType: string | null; siteId: string | null }
interface Operation {
  assetId: string
  title: string
  description: string
  requiresPhoto: boolean
  expectedOutcome: string
}

const STEPS = ['Info generale', 'Operazioni', 'Riepilogo']
const emptyOp: Operation = { assetId: '', title: '', description: '', requiresPhoto: false, expectedOutcome: '' }

export function ChecklistWizardPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [step, setStep] = useState(0)
  const [info, setInfo] = useState({ name: '', description: '', customerId: '', siteId: '' })
  const [operations, setOperations] = useState<Operation[]>([])
  const [newOp, setNewOp] = useState<Operation>(emptyOp)

  const { data: sites = [] } = useQuery({
    queryKey: ['sites', info.customerId],
    queryFn: async () => {
      const params = info.customerId ? { companyId: info.customerId } : {}
      return (await api.get<Site[]>('/sites', { params })).data
    },
    enabled: Boolean(info.customerId),
  })
  const { data: companies = [] } = useQuery({
    queryKey: ['companies', 'list'],
    queryFn: async () => (await api.get<{ id: string; name: string }[]>('/companies')).data,
  })
  const { data: assets = [] } = useQuery({
    queryKey: ['assets', info.siteId],
    queryFn: async () =>
      (await api.get<Asset[]>('/assets', { params: info.siteId ? { siteId: info.siteId } : {} })).data,
    enabled: step >= 1,
  })

  const createMutation = useMutation({
    mutationFn: async () => {
      const template = await api.post<{ id: string }>('/checklist-templates', info)
      await Promise.all(
        operations.map((op, idx) =>
          api.post(`/checklist-templates/${template.data.id}/operations`, { ...op, stepOrder: idx })
        )
      )
      return template.data.id
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['checklist-templates'] })
      navigate('/maintenance/checklists')
    },
  })

  function addOperation() {
    if (!newOp.title) return
    setOperations(p => [...p, { ...newOp }])
    setNewOp(emptyOp)
  }

  function removeOperation(idx: number) {
    setOperations(p => p.filter((_, i) => i !== idx))
  }

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <PageHeader title="Nuovo template checklist" subtitle="Wizard creazione guidata" />

      {/* Step indicator */}
      <div className="flex items-center gap-2">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <div
              className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${
                i < step
                  ? 'bg-green-600 text-white'
                  : i === step
                  ? 'bg-[var(--fc-primary)] text-white'
                  : 'bg-[var(--fc-border)] text-[var(--fc-foreground-muted)]'
              }`}
            >
              {i < step ? <Check className="h-4 w-4" /> : i + 1}
            </div>
            <span className={`text-sm ${i === step ? 'font-semibold text-[var(--fc-foreground)]' : 'text-[var(--fc-foreground-muted)]'}`}>
              {s}
            </span>
            {i < STEPS.length - 1 && <div className="mx-2 h-px w-8 bg-[var(--fc-border)]" />}
          </div>
        ))}
      </div>

      {/* Step 0: Info */}
      {step === 0 && (
        <Card>
          <h3 className="text-base font-semibold mb-4">Informazioni template</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-[var(--fc-foreground-muted)] mb-1">Nome template *</label>
              <input
                value={info.name}
                onChange={e => setInfo(p => ({ ...p, name: e.target.value }))}
                className="w-full rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm"
                placeholder="es. Manutenzione trimestrale impianti termici"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--fc-foreground-muted)] mb-1">Descrizione</label>
              <textarea
                value={info.description}
                onChange={e => setInfo(p => ({ ...p, description: e.target.value }))}
                rows={2}
                className="w-full rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-[var(--fc-foreground-muted)] mb-1">Cliente</label>
                <select
                  value={info.customerId}
                  onChange={e => setInfo(p => ({ ...p, customerId: e.target.value, siteId: '' }))}
                  className="w-full rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm"
                >
                  <option value="">— tutti —</option>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--fc-foreground-muted)] mb-1">Sito</label>
                <select
                  value={info.siteId}
                  onChange={e => setInfo(p => ({ ...p, siteId: e.target.value }))}
                  className="w-full rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm"
                >
                  <option value="">— tutti —</option>
                  {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Step 1: Operations */}
      {step === 1 && (
        <div className="space-y-4">
          <Card>
            <h3 className="text-base font-semibold mb-4">Aggiungi operazione</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-[var(--fc-foreground-muted)] mb-1">Asset</label>
                <select
                  value={newOp.assetId}
                  onChange={e => setNewOp(p => ({ ...p, assetId: e.target.value }))}
                  className="w-full rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm"
                >
                  <option value="">— nessun asset specifico —</option>
                  {assets.map(a => (
                    <option key={a.id} value={a.id}>
                      {a.name}{a.assetType ? ` (${a.assetType})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--fc-foreground-muted)] mb-1">
                  Titolo operazione *
                </label>
                <input
                  value={newOp.title}
                  onChange={e => setNewOp(p => ({ ...p, title: e.target.value }))}
                  className="w-full rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm"
                  placeholder="es. Verifica pressione circuito primario"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--fc-foreground-muted)] mb-1">Esito atteso</label>
                <input
                  value={newOp.expectedOutcome}
                  onChange={e => setNewOp(p => ({ ...p, expectedOutcome: e.target.value }))}
                  className="w-full rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm"
                  placeholder="es. Pressione tra 1.5 e 2.5 bar"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="requiresPhoto"
                  checked={newOp.requiresPhoto}
                  onChange={e => setNewOp(p => ({ ...p, requiresPhoto: e.target.checked }))}
                  className="h-4 w-4 rounded border-[var(--fc-border)]"
                />
                <label htmlFor="requiresPhoto" className="text-sm text-[var(--fc-foreground)]">
                  Foto obbligatoria
                </label>
              </div>
              <button
                onClick={addOperation}
                disabled={!newOp.title}
                className="flex items-center gap-2 rounded-lg bg-[var(--fc-primary)] px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                <Plus className="h-4 w-4" /> Aggiungi
              </button>
            </div>
          </Card>

          {operations.length > 0 && (
            <Card>
              <h3 className="text-base font-semibold mb-3">Operazioni ({operations.length})</h3>
              <ul className="space-y-2">
                {operations.map((op, idx) => (
                  <li
                    key={idx}
                    className="flex items-start gap-3 rounded-lg border border-[var(--fc-border)] p-3"
                  >
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--fc-primary)] text-xs font-bold text-white">
                      {idx + 1}
                    </span>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-[var(--fc-foreground)]">{op.title}</p>
                      {op.expectedOutcome && (
                        <p className="text-xs text-[var(--fc-foreground-muted)]">Esito: {op.expectedOutcome}</p>
                      )}
                      {op.requiresPhoto && (
                        <span className="text-xs text-yellow-600">Foto richiesta</span>
                      )}
                    </div>
                    <button
                      onClick={() => removeOperation(idx)}
                      className="text-[var(--fc-foreground-muted)] hover:text-red-500"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      )}

      {/* Step 2: Summary */}
      {step === 2 && (
        <Card>
          <h3 className="text-base font-semibold mb-4">Riepilogo</h3>
          <dl className="space-y-2 text-sm">
            <div className="flex gap-2">
              <dt className="w-32 font-medium text-[var(--fc-foreground-muted)]">Nome:</dt>
              <dd>{info.name}</dd>
            </div>
            {info.description && (
              <div className="flex gap-2">
                <dt className="w-32 font-medium text-[var(--fc-foreground-muted)]">Descrizione:</dt>
                <dd>{info.description}</dd>
              </div>
            )}
            <div className="flex gap-2">
              <dt className="w-32 font-medium text-[var(--fc-foreground-muted)]">Operazioni:</dt>
              <dd>{operations.length}</dd>
            </div>
          </dl>
          {createMutation.isError && (
            <p className="mt-4 text-sm text-red-600">Errore durante il salvataggio. Riprova.</p>
          )}
        </Card>
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={() => (step === 0 ? navigate('/maintenance/checklists') : setStep(p => p - 1))}
          className="flex items-center gap-2 rounded-lg border border-[var(--fc-border)] px-4 py-2 text-sm"
        >
          <ArrowLeft className="h-4 w-4" /> {step === 0 ? 'Annulla' : 'Indietro'}
        </button>
        {step < 2 ? (
          <button
            onClick={() => setStep(p => p + 1)}
            disabled={step === 0 && !info.name}
            className="flex items-center gap-2 rounded-lg bg-[var(--fc-primary)] px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            Avanti <ArrowRight className="h-4 w-4" />
          </button>
        ) : (
          <button
            onClick={() => createMutation.mutate()}
            disabled={createMutation.isPending}
            className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            <Check className="h-4 w-4" />
            {createMutation.isPending ? 'Salvataggio...' : 'Crea template'}
          </button>
        )}
      </div>
    </div>
  )
}
